import cn from 'classnames'
import { FC, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { Tooltip } from 'core-components/library/tooltip'

import { IWelcomeFlowPartner } from '../../../api/types'
import { PARTNER_SCOPE_TRANSLATION_KEYS } from '../../../configs/translations'
import { selectWelcomeFlowUserData } from '../../../model/selectors'
import { PencilIcon, TrashIcon, UnassignIcon, WarningIcon } from '../../icons'
import { StarIcon } from '../../icons/starIcon'
import { AmplifyTrack } from '../amplifyTrack/AmplifyTrack'
import { PartnerIcons } from '../partnerIcons/PartnerIcons'

import styles from './PartnerCard.module.scss'

interface IPartnerCardProps {
  partner: IWelcomeFlowPartner
  onCreateUpdateRequest: (partner: IWelcomeFlowPartner) => Promise<void>
  onCreateRemoveRequest: (partner: IWelcomeFlowPartner) => Promise<void>
  onRemoveRequest: (requestId: number) => void
}

export const PartnerCard: FC<IPartnerCardProps> = ({
  partner,
  onCreateRemoveRequest,
  onCreateUpdateRequest,
  onRemoveRequest,
}) => {
  const isAction = useMemo(() => partner.STRequestAction !== undefined, [partner.STRequestAction])

  const isAddAction = useMemo(() => partner.STRequestAction === 'Add', [partner.STRequestAction])
  const isRemoveAction = useMemo(
    () => partner.STRequestAction === 'Delete',
    [partner.STRequestAction]
  )
  const isUpdateAction = useMemo(
    () => partner.STRequestAction === 'Update',
    [partner.STRequestAction]
  )

  const userData = useSelector(selectWelcomeFlowUserData)

  const isRoleMatchUserData = useMemo(() => {
    return userData.Roles.includes(partner.STRole)
  }, [userData.Roles, partner.STRole])

  const isBusinessUnitMatchUserData = useMemo(() => {
    const userDatBU = userData.BusinessUnit || ''
    const userDataBUs = userDatBU.split('/')
    const partnerBU = partner.STBusinessUnit ? partner.STBusinessUnit.split('/') : []

    return partnerBU.every((bu) => userDataBUs.includes(bu))
  }, [userData.BusinessUnit, partner.STBusinessUnit])

  const isDataDiscrepancy = useMemo(() => {
    return !isRoleMatchUserData || !isBusinessUnitMatchUserData
  }, [isRoleMatchUserData, isBusinessUnitMatchUserData])

  const discrepancyTranslation = useMemo(() => {
    if (!isRoleMatchUserData && !isBusinessUnitMatchUserData) {
      return PARTNER_SCOPE_TRANSLATION_KEYS.partnerCardActions.allDiscrepancy
    }

    if (!isRoleMatchUserData) {
      return PARTNER_SCOPE_TRANSLATION_KEYS.partnerCardActions.roleDiscrepancy
    }

    if (!isBusinessUnitMatchUserData) {
      return PARTNER_SCOPE_TRANSLATION_KEYS.partnerCardActions.buDiscrepancy
    }

    return null
  }, [isRoleMatchUserData, isBusinessUnitMatchUserData])

  const statusTranslation = useMemo(() => {
    if (isAddAction) {
      return PARTNER_SCOPE_TRANSLATION_KEYS.addRequest
    }

    if (isRemoveAction) {
      return PARTNER_SCOPE_TRANSLATION_KEYS.removeRequest
    }

    if (isUpdateAction) {
      return PARTNER_SCOPE_TRANSLATION_KEYS.updateRequest
    }

    return ''
  }, [isAddAction, isRemoveAction, isUpdateAction])

  return (
    <div className={styles.card}>
      <div
        className={cn(styles.cardHeader, {
          [styles.remove]: isRemoveAction,
          [styles.add]: isAddAction,
          [styles.update]: isUpdateAction,
          [styles.different]: !isAction && isDataDiscrepancy,
        })}
      >
        <div className={styles.statusWrapper}>
          {!isAction && discrepancyTranslation && (
            <div className={styles.dataDiscrepancy}>
              <Tooltip
                tipContent={() => (
                  <Translation
                    text={PARTNER_SCOPE_TRANSLATION_KEYS.partnerCardActions.dataDiffersTooltip}
                  />
                )}
              >
                <div className={styles.warningIcon}>
                  <WarningIcon />
                </div>
              </Tooltip>
              <div className={styles.statusContainer}>
                <div className={styles.status}>
                  <Translation text={discrepancyTranslation} />
                </div>
              </div>
            </div>
          )}
          {isAction && (
            <div className={styles.statusContainer}>
              <div className={styles.status}>
                <Translation text={statusTranslation} />
              </div>
            </div>
          )}
        </div>
        <div className={styles.cardControls}>
          {!isAction && (
            <Tooltip
              tipContent={() => (
                <Translation
                  text={PARTNER_SCOPE_TRANSLATION_KEYS.partnerCardActions.notMyPartner}
                />
              )}
            >
              <Button
                className={cn(styles.veneerLinkButton, {
                  [styles.differentDataIcon]: isDataDiscrepancy,
                })}
                variant="text"
                color="grey"
                onClick={() => onCreateRemoveRequest(partner)}
              >
                <UnassignIcon />
              </Button>
            </Tooltip>
          )}
          {!isAction && (
            <Tooltip
              tipContent={() => (
                <Translation text={PARTNER_SCOPE_TRANSLATION_KEYS.partnerCardActions.edit} />
              )}
            >
              <Button
                className={cn(styles.veneerLinkButton, {
                  [styles.differentDataIcon]: isDataDiscrepancy,
                })}
                variant="text"
                color="grey"
                onClick={() => onCreateUpdateRequest(partner)}
              >
                <PencilIcon />
              </Button>
            </Tooltip>
          )}
          {isAction && (
            <Tooltip
              tipContent={() => (
                <Translation
                  text={PARTNER_SCOPE_TRANSLATION_KEYS.partnerCardActions.removeRequest}
                />
              )}
            >
              <Button
                className={cn(styles.veneerLinkButton)}
                variant="text"
                color="grey"
                onClick={() => onRemoveRequest(partner.STRequestId)}
              >
                <TrashIcon />
              </Button>
            </Tooltip>
          )}
        </div>
      </div>
      <div
        className={cn(styles.cardContent, {
          [styles.remove]: isRemoveAction,
          [styles.add]: isAddAction,
        })}
      >
        <div className={styles.infoWrapper}>
          <div className={styles.regionAndCountry}>
            <span>
              {partner.GeoRegion}, {partner.Country}
            </span>
            {partner.HierarchyType == 'Headquarters' && (
              <div className={styles.headquarters}>HQ</div>
            )}
          </div>
          <div className={styles.partnerName}>{partner.PartnerName}</div>
          <div className={styles.row}>
            <div className={styles.pid}>
              <span>PID</span>
              <span>{partner.PartnerId}</span>
            </div>
            <div className={styles.lid}>
              <span>Loc.ID</span>
              <span>{partner.LocationId}</span>
            </div>
          </div>
          <div className={styles.amplifyTrack}>
            <AmplifyTrack value={partner.AmplifyTrack} />
          </div>
        </div>
        <div className={styles.footer}>
          <div className={styles.icons}>
            <PartnerIcons partner={partner} />
          </div>
          {!!partner.STLeadPBM && (
            <div className={styles.lead}>
              <span>Lead</span>
              <StarIcon />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
